import { fetchListingCollections } from '../../ducks/listingCollections.duck';
import { homePageCollections } from '../../config/configListingCollections';
import { HOME_WISHLIST_SLUGS } from '../../config/configHomePage';
import { COLLECTION_SOURCES } from '../../util/listingCollections';
import { storableError } from '../../util/errors';
import { get } from '../../util/api';
import { regionCollection } from '../LandingPages/RegionLandingPage.duck';

const ZUERICH_SLUG = 'zuerich';

// Below this a row looks broken rather than short, so it hides instead.
const MIN_LISTINGS = 3;

// ================ Action types ================ //

export const FETCH_WISHLIST_FEEDS_REQUEST = 'app/HomePage/FETCH_WISHLIST_FEEDS_REQUEST';
export const FETCH_WISHLIST_FEEDS_SUCCESS = 'app/HomePage/FETCH_WISHLIST_FEEDS_SUCCESS';
export const FETCH_WISHLIST_FEEDS_ERROR = 'app/HomePage/FETCH_WISHLIST_FEEDS_ERROR';

// ================ Reducer ================ //

const initialState = { wishlistFeeds: [], inProgress: false, error: null };

export default function homePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_WISHLIST_FEEDS_REQUEST:
      return { ...state, inProgress: true, error: null };
    case FETCH_WISHLIST_FEEDS_SUCCESS:
      return { ...state, wishlistFeeds: payload, inProgress: false };
    case FETCH_WISHLIST_FEEDS_ERROR:
      return { ...state, wishlistFeeds: [], inProgress: false, error: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const selectWishlistFeeds = state => state.HomePage?.wishlistFeeds || [];

// ================ Helpers ================ //

/**
 * The configured wishlists as listing collections, in the order configHomePage lists
 * them. A slug that isn't public, or a wishlist that holds nothing, yields no row.
 *
 * @param {Array} wishlists payload of /api/wishlists/discover
 * @returns {Array} collection descriptors
 */
export const buildWishlistFeeds = (wishlists = []) => {
  const bySlug = new Map(wishlists.map(w => [w.publicSlug, w]));

  return HOME_WISHLIST_SLUGS.map(slug => bySlug.get(slug))
    .filter(wishlist => wishlist?.items?.length > 0)
    .map(wishlist => ({
      id: `wishlist-${wishlist.publicSlug}`,
      source: COLLECTION_SOURCES.QUERY,
      title: wishlist.title,
      params: { ids: wishlist.items.map(item => item.listingId) },
      limit: 8,
      minListings: MIN_LISTINGS,
      footerLink: {
        link: { name: 'PublicWishlistPage', params: { slug: wishlist.publicSlug } },
        label: 'Alle ansehen',
      },
    }));
};

/**
 * The feeds that need no request: "Neu auf Toastably" and the canton row.
 *
 * @param {Object} config marketplace configuration
 * @returns {Array} collection descriptors
 */
export const homeCollections = config => {
  const zuerich = regionCollection(ZUERICH_SLUG, config);
  const zuerichMaybe = zuerich
    ? [
        {
          ...zuerich,
          limit: 8,
          title: 'Hochzeiten im Kanton Zürich',
          footerLink: {
            link: { name: 'RegionLandingPage', params: { slug: ZUERICH_SLUG } },
            label: 'Alle ansehen',
          },
        },
      ]
    : [];
  return [...homePageCollections, ...zuerichMaybe].map(c => ({ minListings: MIN_LISTINGS, ...c }));
};

// ================ Thunks ================ //

/**
 * Fetch the curated wishlists in one request.
 *
 * One request rather than one per slug: on a cold cache the server scans every user,
 * and its in-flight guard hands concurrent callers an empty list - four parallel
 * per-slug requests would come back empty for three of them.
 *
 * Never rejects. That scan has its own timeout and can return partial results, so a
 * slow or failing scan costs the rows, not the page.
 */
export const fetchWishlistFeeds = () => dispatch => {
  dispatch({ type: FETCH_WISHLIST_FEEDS_REQUEST });

  return get('/api/wishlists/discover?perPage=60')
    .then(response => {
      const feeds = buildWishlistFeeds(response?.data || []);
      dispatch({ type: FETCH_WISHLIST_FEEDS_SUCCESS, payload: feeds });
      return feeds;
    })
    .catch(e => {
      dispatch({ type: FETCH_WISHLIST_FEEDS_ERROR, payload: storableError(e) });
      return [];
    });
};

export const loadData = (params, search, config) => dispatch =>
  // Fetched here rather than in the component so the cards are server-rendered.
  dispatch(fetchWishlistFeeds()).then(wishlistFeeds =>
    dispatch(fetchListingCollections([...homeCollections(config), ...wishlistFeeds], { config }))
  );
